import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { IconX } from "@tabler/icons-react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import service from "utils/service";
import useFetchData from "utils/customFetchData";
import { reduxSliceData } from "store/slices/reduxData";
import useClientConfig from "hooks/useClientConfig";

interface CreateCustomerModalProps {
  visible: boolean;
  onClose: () => void;
}

const RechargeWalletRazorpay: React.FC<CreateCustomerModalProps> = ({
  visible,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const loginCustomer = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("loginCustomer") || "[]");
    } catch {
      return [];
    }
  }, []);
  const [discount, setDiscount] = useState(0);
  const reduxBag = useSelector((state: any) => state.reduxData?.reduxData ?? {});
  const getWalletData = reduxBag?.getWalletData;
  const [balance, setBalance] = useState(0);
  const [loader, setLoader] = useState(false);
  const { addData } = useFetchData();
  const dispatch = useDispatch();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [amount, setAmount] = useState(1500);
  const predefinedAmounts = [500, 1000, 1500, 5000, 10000];
  const clientConfig = useClientConfig();

  const companyName = clientConfig.branding.companyName || "Wallet";
  const customerId = loginCustomer?.[0]?.UserData?.[0]?.CUSTOMER_ID;

  useEffect(() => {
    if (getWalletData) {
      const walletRows = Array.isArray(getWalletData?.data)
        ? getWalletData.data
        : Array.isArray(getWalletData?.apiData)
          ? getWalletData.apiData
          : Array.isArray(getWalletData)
            ? getWalletData
            : [];
      const fetchedBalance = walletRows
        .map((item: any) => Number(item.BALANCE || 0))
        .reduce((acc: number, walletBalance: number) => acc + walletBalance, 0);
      setBalance(fetchedBalance);
    }
  }, [getWalletData]);

  // Load Razorpay Script dynamically
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => undefined;
    script.onerror = () => enqueueSnackbar("Failed to load Razorpay script", { variant: "error" });
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, [enqueueSnackbar]);

  // ✅ Function: Create Razorpay Order via Backend
  const createRazorpayOrder = async () => {
    try {
      const walletMaster = (service as any)?.API_URL?.wallet_master;
      if (!walletMaster?.create) {
        enqueueSnackbar("Wallet payment API is not configured.", { variant: "error" });
        return null;
      }

      if (!customerId) {
        enqueueSnackbar('Customer profile is missing.', { variant: 'error' });
        return null;
      }

      const body = {
        custId: customerId,
        amount: amount,
      };
      const response = await addData(body, walletMaster.create);
      const responseData = response?.apiData ?? response?.data ?? response;

      if ((response?.code === 200 || response?.statusCode === 200 || response?.success === true) && responseData?.order?.id) {
        return responseData.order;
      } else {
        enqueueSnackbar("Failed to create payment order", { variant: "error" });
        return null;
      }
    } catch (error) {
      enqueueSnackbar("Something went wrong while creating order", { variant: "error" });
      return null;
    }
  };

   // ✅ Function: Verify Payment via backend
  const verifyPayment = async (response: any) => {
    try {
      const walletMaster = (service as any)?.API_URL?.wallet_master;
      if (!walletMaster?.verify) {
        enqueueSnackbar("Wallet payment API is not configured.", { variant: "error" });
        return;
      }

      const verifyRes = await addData(response, walletMaster.verify);
      const verifyData = verifyRes?.apiData ?? verifyRes?.data ?? verifyRes;

      if (verifyRes?.code === 200 || verifyRes?.statusCode === 200 || verifyRes?.success === true || verifyData?.success === true) {
        enqueueSnackbar("Payment Successful!", { variant: "success" });
        setShowSuccessModal(true);
        dispatch(reduxSliceData({ key: "paymentSuccess", data: true }));
      } else {
        enqueueSnackbar("Payment verification failed!", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Error verifying payment", { variant: "error" });
    }
  };

  // Handle Razorpay Payment
const handleRazorpayPayment = useCallback(async () => {
    setLoader(true);

    try {
      // Step 1: Create order from backend
      const order = await createRazorpayOrder();
      if (!order) return;
      // Step 2: Configure Razorpay Checkout
      const options = {
        key: import.meta.env.VITE_RAZORPAY_KEY_ID, // 🗝️ Public key only
        amount: order.amount, // in paise from backend
        currency: order.currency,
        name: `${companyName} Wallet Recharge`,
        description: "Wallet Recharge",
        order_id: order.id, // <-- must use backend-created order_id
        handler: async function (response: any) {
          // Step 3: Verify Payment (optional but recommended)
          await verifyPayment(response);
        },
        prefill: {
          name: loginCustomer[0].UserData[0].NAME,
          email: loginCustomer[0].UserData[0].EMAIL_ID,
          contact: loginCustomer[0].UserData[0].MOBILE_NO,
        },
        theme: { color: "#5D4BFE" },
      };

      const razorpay = new (window as any).Razorpay(options);
      razorpay.open();
    } catch (error) {
      enqueueSnackbar("Error initiating payment", { variant: "error" });
    } finally {
      setLoader(false);
    }
  }, [amount, companyName, createRazorpayOrder, enqueueSnackbar, loginCustomer]);

  // Add Wallet Balance after successful payment
  const addWalletBalance = async (transactionId?: string) => {
    setLoader(true);
    const body = {
      CUSTOMER_ID: customerId,
      AMOUNT: amount,
      TRANSACTION_ID: transactionId,
    };
    const walletData = await addData(
      body,
      (service as any)?.API_URL?.wallet_master?.add
    );
    if (walletData.code === 200) {
      setLoader(false);
      dispatch(reduxSliceData({ key: "paymentSuccess", data: true }));
    }
  };

  const handleFormSubmit = useCallback(() => {
    onClose();
    handleRazorpayPayment();
  }, [handleRazorpayPayment, onClose]);

  return (
    <>
      <Dialog open={visible} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 7 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Recharge Your Wallet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Current Wallet Amt: ₹{balance.toFixed(2)}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small" aria-label="close wallet dialog">
              <IconX stroke={1.5} size="18px" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2.5} component="form" onSubmit={(event) => { event.preventDefault(); void handleFormSubmit(); }}>
            <Paper variant="outlined" sx={{ p: 2, borderColor: 'primary.light', bgcolor: 'primary.50' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Enter amount in multiples of 100
              </Typography>

              <TextField
                label="Amount"
                type="number"
                value={amount}
                onChange={(event) => setAmount(Number(event.target.value || 0))}
                inputProps={{ min: 500, max: 50000, step: 100 }}
                helperText="Min value: ₹500 & Max Value: ₹50,000"
                fullWidth
              />

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
                {predefinedAmounts.map((presetAmount) => (
                  <Chip
                    key={presetAmount}
                    label={`₹ ${presetAmount}`}
                    variant={amount === presetAmount ? 'filled' : 'outlined'}
                    color="primary"
                    onClick={() => setAmount(presetAmount)}
                    clickable
                  />
                ))}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Recharge Amount</Typography>
                  <Typography variant="body2">₹ {amount}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Coupon Discount</Typography>
                  <Typography variant="body2">₹ {discount}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Total Amount To Be Credited</Typography>
                  <Typography variant="body2">₹ {amount + discount}</Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Payable Amount</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>₹ {amount}</Typography>
                </Stack>
              </Stack>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loader}
                sx={{ minWidth: 220, borderRadius: 2 }}
              >
                {loader ? 'Processing...' : 'Continue to Payment'}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          onClose();
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Payment Successful</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Your payment was successful and the amount will be added to your wallet shortly.
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RechargeWalletRazorpay;
